import { useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { flushSync } from 'react-dom';
import { ActionButton, ActionTextarea } from '../parts/ActionButton.js';
import { useViewing } from '../view-as.js';
import { ComposerIcon, type FormatName } from './ComposerIcon.js';
import { FormatBar } from './FormatBar.js';
import { EmojiButton } from './EmojiButton.js';
import { cleanNodes, editorMessage, formatRange, insertNodes, selectRange, type MessageBody } from './editor-dom.js';

export function useMessageEditor({ text, setText, busy, placeholder = 'Type a message', onSend, onFile }: {
  text: string; setText: (text: string) => void; busy: boolean; placeholder?: string; onSend: () => void; onFile?: (file: File) => void;
}) {
  const viewing = useViewing();
  const disabled = busy || !!viewing;
  const [rich, setRich] = useState(false);
  const [bar, setBar] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const editor = useRef<HTMLDivElement>(null);
  const saved = useRef<Range | null>(null);
  const cursor = useRef({ start: 0, end: 0 });
  const remember = () => {
    if (editor.current) {
      const selection = window.getSelection();
      if (selection?.rangeCount && editor.current.contains(selection.getRangeAt(0).commonAncestorContainer)) saved.current = selection.getRangeAt(0).cloneRange();
    } else if (textarea.current) cursor.current = { start: textarea.current.selectionStart, end: textarea.current.selectionEnd };
  };
  const range = () => {
    const root = editor.current!;
    if (saved.current && root.contains(saved.current.commonAncestorContainer)) return saved.current.cloneRange();
    const next = document.createRange(); next.selectNodeContents(root); next.collapse(false); return next;
  };
  const focus = () => {
    if (editor.current) { editor.current.focus(); selectRange(range()); }
    else { textarea.current?.focus(); textarea.current?.setSelectionRange(cursor.current.start, cursor.current.end); }
  };
  const ensureRich = () => {
    remember();
    if (!rich) {
      flushSync(() => setRich(true));
      const root = editor.current!;
      const node = document.createTextNode(text); root.replaceChildren(node);
      const next = document.createRange();
      next.setStart(node, Math.min(cursor.current.start, text.length)); next.setEnd(node, Math.min(cursor.current.end, text.length));
      saved.current = next;
    }
    editor.current!.focus(); selectRange(range());
  };
  const sync = () => { if (editor.current) { setText(editorMessage(editor.current).text); remember(); } };
  const apply = (name: FormatName, href?: string) => {
    if (disabled) return;
    ensureRich();
    const tag = { Bold: 'b', Italic: 'i', Underline: 'u', Strikethrough: 's', 'Bulleted list': 'ul', 'Numbered list': 'ol', 'Insert link': 'a' } as const;
    formatRange(editor.current!, range(), tag[name], href); sync();
  };
  const insertEmoji = (emoji: string) => {
    if (disabled) return;
    if (rich) { editor.current!.focus(); insertNodes(range(), document.createTextNode(emoji)); sync(); }
    else {
      const { start, end } = cursor.current;
      const value = text.slice(0, start) + emoji + text.slice(end);
      flushSync(() => setText(value));
      cursor.current = { start: start + emoji.length, end: start + emoji.length }; focus();
    }
  };
  const keyDown = (e: KeyboardEvent) => {
    if (disabled || e.nativeEvent.isComposing) return;
    const key = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && ['b', 'i', 'u'].includes(key)) {
      e.preventDefault(); apply(({ b: 'Bold', i: 'Italic', u: 'Underline' } as const)[key as 'b' | 'i' | 'u']); return;
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
    if (e.key === 'Escape') { clear(); }
  };
  const paste = (e: ClipboardEvent) => {
    if (disabled) { e.preventDefault(); return; }
    const file = e.clipboardData.files?.[0];
    if (file && onFile) { e.preventDefault(); onFile(file); return; }
    if (!rich) return;
    e.preventDefault(); remember();
    const html = e.clipboardData.getData('text/html');
    const nodes = html ? cleanNodes(new DOMParser().parseFromString(html, 'text/html').body) : document.createTextNode(e.clipboardData.getData('text/plain'));
    insertNodes(range(), nodes); sync();
  };
  const clear = () => {
    setText(''); editor.current?.replaceChildren(); saved.current = null; cursor.current = { start: 0, end: 0 };
  };
  const message = (): MessageBody => rich && editor.current ? editorMessage(editor.current) : { text: text.trim() };
  return {
    message, clear,
    bar: bar ? <FormatBar disabled={disabled} onFormat={apply} onRemember={remember} onCloseLink={focus} /> : null,
    field: <span className="field wide">{rich ? <div ref={editor} className="ch-compose-t ch-rich-editor" contentEditable={!disabled} suppressContentEditableWarning
      role="textbox" aria-multiline="true" aria-label="Type a message" aria-placeholder="Type a message" data-placeholder="Type a message" aria-disabled={disabled}
      onInput={sync} onMouseUp={remember} onKeyUp={remember} onBlur={remember} onPaste={paste} onKeyDown={keyDown} />
      : <ActionTextarea ref={textarea} className="ch-compose-t" rows={1} value={text} disabled={busy} maxLength={8000}
        placeholder={placeholder} aria-label="Type a message" onChange={e => setText(e.target.value)} onSelect={remember} onBlur={remember} onPaste={paste} onKeyDown={keyDown} />}</span>,
    controls: <>
      <ActionButton type="button" className="ch-compose-icon" aria-label="Format" title="Format" aria-expanded={bar} disabled={busy}
        onMouseDown={e => e.preventDefault()} onClick={() => { ensureRich(); setBar(v => !v); }}><ComposerIcon name="Format" /></ActionButton>
      <EmojiButton disabled={disabled} onOpen={remember} onPick={insertEmoji} onClose={focus} />
    </>,
  };
}
